import type { KataScoringDefinition, ScoringCriterion } from './types'

function c(
  id: string,
  text: string,
  suggested: ScoringCriterion['suggested'],
  hint?: string,
): ScoringCriterion {
  return hint ? { id, text, suggested, hint } : { id, text, suggested }
}

const generalCeremony: ScoringCriterion[] = [
  c('kime-gen-start-pos', 'Tori decides the starting position and Uke takes the right distance', 'medium'),
  c('kime-gen-weapons-place', 'Way of correctly placing and collecting the weapons', 'medium'),
  c(
    'kime-gen-weapons-bow',
    'Before Tori–Uke bow: Uke lays weapons on the mat to his right (sword then dagger), parallel, cutting edge towards him; pickup order dagger then sword',
    'small_medium',
  ),
  c(
    'kime-gen-weapons-walk',
    'Uke stands, turns, walks ~1.8 m, sits and holds weapons vertically, then lays them parallel to the tatami line (dagger inside, edges towards him)',
    'small_medium',
  ),
  c(
    'kime-gen-weapons-end',
    'At kata end Uke picks up weapons (dagger then sword) and carries them on his right side',
    'small_medium',
  ),
  c('kime-gen-attack-distance', 'Distance of the attack', 'small_medium'),
  c('kime-gen-idori-toes', 'Idori: during attack, feet on toes except Ryote-dori', 'medium'),
  c(
    'kime-gen-arm-lock-steps',
    "Tori's displacement when performing an arm lock: right foot – left foot – right foot / left foot – right foot – left foot",
    'medium',
  ),
  c('kime-gen-weapons-general', 'Weapons general use', 'medium'),
  c('kime-gen-weapons-technique', 'Weapons use in each technique', 'small_medium'),
  c('kime-gen-direction', 'Direction when performing a technique', 'medium'),
  c('kime-gen-arm-lock-look', 'Tori looks straight to his front when performing an arm lock', 'small'),
  c('kime-gen-kiai', 'Kiai', 'medium', 'Small if unnecessary; Medium if absent'),
  c('kime-gen-submission', 'Submitting (tapping twice)', 'small'),
]

/** Practice judging: 21 scored elements (max 210); opening/closing bows are catalog-only. */
export const kimeNoKataScoring: KataScoringDefinition = {
  kataId: 'kime-no-kata',
  maxPerJudge: 210,
  maxPanelOfThree: 630,
  elements: [
    {
      id: 'kime-general',
      kind: 'ceremony',
      name: 'General elements',
      nameJa: '一般観察事項',
      criteria: generalCeremony,
    },
    {
      id: 'kime-idori-ryote-dori',
      kind: 'technique',
      name: 'Ryote-dori',
      nameJa: '両手取',
      techniqueId: 'kime-idori-ryote-dori',
      groupId: 'idori',
      criteria: [
        c(
          'kime-idori-ryote-dori-1',
          'Distance ~90 cm first; Tori and Uke slide forward to hiza-zume (two fists)',
          'small_medium',
        ),
        c('kime-idori-ryote-dori-2', 'Uke grabs Tori’s wrists without coming on his toes (kiai)', 'small_medium'),
        c(
          'kime-idori-ryote-dori-3',
          'Tori on toes, knees together, kicks Uke in the solar plexus (kiai); right knee down, left knee raised',
          'small_medium',
        ),
        c(
          'kime-idori-ryote-dori-4',
          'Tori takes Uke’s left wrist, turns left, pulls arm forward diagonally, traps under armpit, Ude-hishigi-waki-gatame',
          'small_medium',
        ),
        c('kime-idori-ryote-dori-5', 'Uke stays on knees and taps Tori’s leg twice to submit', 'small_medium'),
      ],
    },
    {
      id: 'kime-idori-tsukkake',
      kind: 'technique',
      name: 'Tsukkake',
      nameJa: '突掛',
      techniqueId: 'kime-idori-tsukkake',
      groupId: 'idori',
      criteria: [
        c('kime-idori-tsukkake-1', 'Distance: hiza-zume', 'small_medium'),
        c(
          'kime-idori-tsukkake-2',
          'Uke on toes, reversed right fist punch at solar plexus (kiai)',
          'small_medium',
        ),
        c(
          'kime-idori-tsukkake-3',
          'Tori on toes, pivots 90° right on left knee, deflects with left hand, atemi reversed right fist between eyebrows (kiai)',
          'small_medium',
        ),
        c(
          'kime-idori-tsukkake-4',
          'Tori grabs right wrist to right hip, left hand on collar, two steps diagonally, Ude-hishigi-hara-gatame and neck control',
          'small_medium',
        ),
        c('kime-idori-tsukkake-5', 'Uke stays on knees and taps mat twice', 'small_medium'),
      ],
    },
    {
      id: 'kime-idori-suri-age',
      kind: 'technique',
      name: 'Suri-age',
      nameJa: '摺上',
      techniqueId: 'kime-idori-suri-age',
      groupId: 'idori',
      criteria: [
        c('kime-idori-suri-age-1', 'Distance: hiza-zume', 'small_medium'),
        c(
          'kime-idori-suri-age-2',
          'Uke on toes, open-hand thrust at forehead (kiai)',
          'small_medium',
        ),
        c(
          'kime-idori-suri-age-3',
          'Tori defends/grabs wrist, left under armpit, kick solar plexus (kiai), pivot twisting arm face-down',
          'small_medium',
        ),
        c(
          'kime-idori-suri-age-4',
          'Tori pushes arm forward, two knee steps (left/right), left knee on elbow, ude-hishigi-hiza-gatame',
          'small_medium',
        ),
        c('kime-idori-suri-age-5', 'Uke taps mat twice', 'small_medium'),
      ],
    },
    {
      id: 'kime-idori-yoko-uchi',
      kind: 'technique',
      name: 'Yoko-uchi',
      nameJa: '横打',
      techniqueId: 'kime-idori-yoko-uchi',
      groupId: 'idori',
      criteria: [
        c('kime-idori-yoko-uchi-1', 'Distance: hiza-zume', 'small_medium'),
        c('kime-idori-yoko-uchi-2', 'Uke on toes, right fist to left temple (kiai)', 'small_medium'),
        c(
          'kime-idori-yoko-uchi-3',
          'Tori ducks under right arm, steps onto right foot, Kata-gatame control',
          'small_medium',
        ),
        c(
          'kime-idori-yoko-uchi-4',
          'Tori pushes Uke over onto back (left foot), elbow to face, solar plexus elbow strike (kiai)',
          'small_medium',
        ),
        c(
          'kime-idori-yoko-uchi-5',
          'Uke sits toma ~1.2 m facing Tori, walks right around Tori, sits one step rear of Tori',
          'small_medium',
        ),
      ],
    },
    {
      id: 'kime-idori-ushiro-dori',
      kind: 'technique',
      name: 'Ushiro-dori',
      nameJa: '後取',
      techniqueId: 'kime-idori-ushiro-dori',
      groupId: 'idori',
      criteria: [
        c(
          'kime-idori-ushiro-dori-1',
          'Distance: one step behind Tori first, slide forward to ~20 cm behind',
          'small_medium',
        ),
        c('kime-idori-ushiro-dori-2', 'Uke on toes, right foot forward, grapples upper arms (kiai)', 'small_medium'),
        c(
          'kime-idori-ushiro-dori-3',
          'Tori raises arms, grabs sleeves, slides right leg back between Uke’s legs, rolls left (Makikomi-like)',
          'small_medium',
        ),
        c(
          'kime-idori-ushiro-dori-4',
          'Ushiro-kesa-gatame control, groin punch left fist (kiai)',
          'small_medium',
        ),
        c(
          'kime-idori-ushiro-dori-5',
          'Uke returns one step behind, walks right around Tori, sits toma ~1.2 m facing Tori',
          'small_medium',
        ),
      ],
    },
    {
      id: 'kime-idori-tsukkomi',
      kind: 'technique',
      name: 'Tsukkomi',
      nameJa: '突込',
      techniqueId: 'kime-idori-tsukkomi',
      groupId: 'idori',
      criteria: [
        c(
          'kime-idori-tsukkomi-1',
          'Uke collects dagger (right on top, left down), edge up inside jacket; ~90 cm then slide to ~45 cm',
          'small_medium',
        ),
        c(
          'kime-idori-tsukkomi-2',
          'Uke draws dagger on toes, left foot forward, stab at stomach (kiai)',
          'small_medium',
        ),
        c(
          'kime-idori-tsukkomi-3',
          'Tori pivots 90° right, deflects, atemi, wrist to hip, collar grip, two steps, hara-gatame and neck control',
          'small_medium',
        ),
        c('kime-idori-tsukkomi-4', 'Uke on knees, taps mat twice; replaces dagger in jacket', 'small_medium'),
      ],
    },
    {
      id: 'kime-idori-kirikomi',
      kind: 'technique',
      name: 'Kiri-komi',
      nameJa: '切込',
      techniqueId: 'kime-idori-kirikomi',
      groupId: 'idori',
      criteria: [
        c('kime-idori-kirikomi-1', 'Distance ~45 cm', 'small_medium'),
        c(
          'kime-idori-kirikomi-2',
          'Uke transfers dagger to belt as short sword; on toes, right foot forward, slash forehead (kiai)',
          'small_medium',
        ),
        c(
          'kime-idori-kirikomi-3',
          'Tori catches wrist, pivots on left knee, waki-gatame under armpit',
          'small_medium',
        ),
        c(
          'kime-idori-kirikomi-4',
          'Uke taps Tori’s leg twice; toma ~1.2 m; short sword back to jacket as dagger',
          'small_medium',
        ),
      ],
    },
    {
      id: 'kime-idori-yoko-tsuki',
      kind: 'technique',
      name: 'Yoko-tsuki',
      nameJa: '横突',
      techniqueId: 'kime-idori-yoko-tsuki',
      groupId: 'idori',
      criteria: [
        c(
          'kime-idori-yoko-tsuki-1',
          'Uke sits Tori’s right side, slides forward; distance ~20 cm on the side',
          'small_medium',
        ),
        c(
          'kime-idori-yoko-tsuki-2',
          'Uke draws dagger, on toes left foot forward, stab right side edge up (kiai)',
          'small_medium',
        ),
        c(
          'kime-idori-yoko-tsuki-3',
          'Tori pivots 180° right, deflects, atemi, wrist/collar, two steps diagonally, hara-gatame and neck control',
          'small_medium',
        ),
        c(
          'kime-idori-yoko-tsuki-4',
          'Uke taps mat twice; both sit; Uke replaces dagger, walks to toma; replaces dagger on mat in original position',
          'small_medium',
        ),
      ],
    },
    {
      id: 'kime-tachiai-ryote-dori',
      kind: 'technique',
      name: 'Ryote-dori',
      nameJa: '両手取',
      techniqueId: 'kime-tachiai-ryote-dori',
      groupId: 'tachiai',
      criteria: [
        c('kime-tachiai-ryote-dori-1', 'Uke distance one step (~40 cm)', 'small_medium'),
        c('kime-tachiai-ryote-dori-2', 'Uke grabs wrists, right foot half step (kiai)', 'small_medium'),
        c(
          'kime-tachiai-ryote-dori-3',
          'Tori pulls arms back breaking balance, groin kick (kiai), foot down immediately',
          'small_medium',
        ),
        c(
          'kime-tachiai-ryote-dori-4',
          'Standing Ude-hishigi-waki-gatame on left arm diagonally',
          'small_medium',
        ),
        c('kime-tachiai-ryote-dori-5', 'Uke taps Tori’s leg twice; return to start, Uke at toma ~1.2 m', 'small_medium'),
      ],
    },
    {
      id: 'kime-tachiai-sode-tori',
      kind: 'technique',
      name: 'Sode-tori',
      nameJa: '袖取',
      techniqueId: 'kime-tachiai-sode-tori',
      groupId: 'tachiai',
      criteria: [
        c(
          'kime-tachiai-sode-tori-1',
          'Uke walks around Tori’s right (Tori steps slightly back), grabs left sleeve behind, forces forward R-L-R',
          'small_medium',
        ),
        c(
          'kime-tachiai-sode-tori-2',
          'Third step Tori breaks balance right, kicks right knee little-toe side (kiai), pivots 180° left',
          'small_medium',
        ),
        c(
          'kime-tachiai-sode-tori-3',
          'Grabs right sleeve and left collar, throws with O-soto-gari',
          'small_medium',
        ),
      ],
    },
    {
      id: 'kime-tachiai-tsukkake',
      kind: 'technique',
      name: 'Tsukkake',
      nameJa: '突掛',
      techniqueId: 'kime-tachiai-tsukkake',
      groupId: 'tachiai',
      criteria: [
        c('kime-tachiai-tsukkake-1', 'Facing at three steps (~1.8 m)', 'small_medium'),
        c(
          'kime-tachiai-tsukkake-2',
          'Uke left foot feint, lunges right, reversed fist between eyebrows (uto) (kiai)',
          'small_medium',
        ),
        c(
          'kime-tachiai-tsukkake-3',
          'Tori 90° tai-sabaki, grabs forearm near wrist, pulls forward and down',
          'small_medium',
        ),
        c(
          'kime-tachiai-tsukkake-4',
          'Steps behind, Hadaka-jime clasp on left shoulder, choke as Uke pulls back',
          'small_medium',
        ),
        c(
          'kime-tachiai-tsukkake-5',
          'Uke defends pulling down right upper arm; taps mat twice with right foot',
          'small_medium',
        ),
      ],
    },
    {
      id: 'kime-tachiai-tsuki-age',
      kind: 'technique',
      name: 'Tsuki-age',
      nameJa: '突上',
      techniqueId: 'kime-tachiai-tsuki-age',
      groupId: 'tachiai',
      criteria: [
        c('kime-tachiai-tsuki-age-1', 'About one step distance', 'small_medium'),
        c('kime-tachiai-tsuki-age-2', 'Uke right foot forward, uppercut to chin (kiai)', 'small_medium'),
        c(
          'kime-tachiai-tsuki-age-3',
          'Tori leans back, both hands on wrist, pulls up diagonally forward, standing waki-gatame',
          'small_medium',
        ),
        c(
          'kime-tachiai-tsuki-age-4',
          'Uke should not bend too much; taps Tori’s leg twice',
          'small_medium',
        ),
      ],
    },
    {
      id: 'kime-tachiai-suri-age',
      kind: 'technique',
      name: 'Suri-age',
      nameJa: '摺上',
      techniqueId: 'kime-tachiai-suri-age',
      groupId: 'tachiai',
      criteria: [
        c('kime-tachiai-suri-age-1', 'About one step distance', 'small_medium'),
        c(
          'kime-tachiai-suri-age-2',
          'Uke right foot forward, open palm to forehead (kiai)',
          'small_medium',
        ),
        c(
          'kime-tachiai-suri-age-3',
          'Tori leans back, parries at elbow with left forearm, stomach punch reversed right fist (kiai)',
          'small_medium',
        ),
        c('kime-tachiai-suri-age-4', 'Immediately left Uki-goshi', 'small_medium'),
      ],
    },
    {
      id: 'kime-tachiai-yoko-uchi',
      kind: 'technique',
      name: 'Yoko-uchi',
      nameJa: '横打',
      techniqueId: 'kime-tachiai-yoko-uchi',
      groupId: 'tachiai',
      criteria: [
        c('kime-tachiai-yoko-uchi-1', 'About one step distance', 'small_medium'),
        c('kime-tachiai-yoko-uchi-2', 'Uke right foot, fist to left temple (kiai)', 'small_medium'),
        c(
          'kime-tachiai-yoko-uchi-3',
          'Tori ducks with left foot in, pushes chest, grabs collar, steps behind R then L',
          'small_medium',
        ),
        c(
          'kime-tachiai-yoko-uchi-4',
          'Okuri-eri-jime: forehead on back of head, step back left, lower hips',
          'small_medium',
        ),
        c(
          'kime-tachiai-yoko-uchi-5',
          'Uke pulls down left upper arm; submits hitting mat twice with right foot',
          'small_medium',
        ),
      ],
    },
    {
      id: 'kime-tachiai-ke-age',
      kind: 'technique',
      name: 'Ke-age',
      nameJa: '蹴上',
      techniqueId: 'kime-tachiai-ke-age',
      groupId: 'tachiai',
      criteria: [
        c('kime-tachiai-ke-age-1', 'Distance one step', 'small_medium'),
        c('kime-tachiai-ke-age-2', 'Uke small step left foot, groin kick (kiai)', 'small_medium'),
        c(
          'kime-tachiai-ke-age-3',
          'Tori steps back right 90° tai-sabaki, catches ankle, pulls foot left, twists hips, groin kick (kiai)',
          'small_medium',
        ),
        c('kime-tachiai-ke-age-4', 'Face each other at toma ~1.2 m', 'small_medium'),
      ],
    },
    {
      id: 'kime-tachiai-ushiro-dori',
      kind: 'technique',
      name: 'Ushiro-dori',
      nameJa: '後取',
      techniqueId: 'kime-tachiai-ushiro-dori',
      groupId: 'tachiai',
      criteria: [
        c(
          'kime-tachiai-ushiro-dori-1',
          'Uke around Tori’s right to behind ~90 cm; Tori steps backward',
          'small_medium',
        ),
        c(
          'kime-tachiai-ushiro-dori-2',
          'Tori advances L-R-L; on third step Uke grapples upper arms from behind',
          'small_medium',
        ),
        c(
          'kime-tachiai-ushiro-dori-3',
          'Tori elbows out, both hands on upper right sleeve, Seoi-nage',
          'small_medium',
        ),
        c(
          'kime-tachiai-ushiro-dori-4',
          'Right knee down, left up, Te-gatana between eyes (kiai); Uke at toma ~1.2 m',
          'small_medium',
        ),
      ],
    },
    {
      id: 'kime-tachiai-tsukkomi',
      kind: 'technique',
      name: 'Tsukkomi',
      nameJa: '突込',
      techniqueId: 'kime-tachiai-tsukkomi',
      groupId: 'tachiai',
      criteria: [
        c(
          'kime-tachiai-tsukkomi-1',
          'Uke collects dagger edge up in jacket; distance ~90 cm',
          'small_medium',
        ),
        c(
          'kime-tachiai-tsukkomi-2',
          'Uke draws dagger, left foot forward, stab solar plexus (kiai)',
          'small_medium',
        ),
        c(
          'kime-tachiai-tsukkomi-3',
          'Tori 90° tai-sabaki, deflect, atemi, wrist to hip, collar, two steps diagonally, hara-gatame',
          'small_medium',
        ),
        c(
          'kime-tachiai-tsukkomi-4',
          'Uke does not bend too much; taps leg twice; replaces dagger',
          'small_medium',
        ),
      ],
    },
    {
      id: 'kime-tachiai-kirikomi',
      kind: 'technique',
      name: 'Kiri-komi',
      nameJa: '切込',
      techniqueId: 'kime-tachiai-kirikomi',
      groupId: 'tachiai',
      criteria: [
        c(
          'kime-tachiai-kirikomi-1',
          'Uke ~90 cm: dagger to belt as short sword; right foot forward, slash forehead (kiai)',
          'small_medium',
        ),
        c(
          'kime-tachiai-kirikomi-2',
          'Tori both hands on wrist, waki-gatame moving back diagonally',
          'small_medium',
        ),
        c(
          'kime-tachiai-kirikomi-3',
          'Uke should not bend too much; taps leg twice; toma ~1.2 m; short sword to belt then dagger in jacket',
          'small_medium',
        ),
      ],
    },
    {
      id: 'kime-tachiai-nuki-kake',
      kind: 'technique',
      name: 'Nuki-kake',
      nameJa: '抜掛',
      techniqueId: 'kime-tachiai-nuki-kake',
      groupId: 'tachiai',
      criteria: [
        c(
          'kime-tachiai-nuki-kake-1',
          'Uke replaces dagger on mat, collects sword to left belt edge up',
          'small_medium',
        ),
        c('kime-tachiai-nuki-kake-2', 'At toma Uke right foot, attempts to draw sword', 'small_medium'),
        c(
          'kime-tachiai-nuki-kake-3',
          'Tori right foot close, blocks right wrist to stop draw',
          'small_medium',
        ),
        c(
          'kime-tachiai-nuki-kake-4',
          'Left foot behind Uke, collar grip, step back diagonally, Kataha-jime',
          'small_medium',
        ),
        c('kime-tachiai-nuki-kake-5', 'Uke submits banging mat twice with right foot', 'small_medium'),
      ],
    },
    {
      id: 'kime-tachiai-kiri-oroshi',
      kind: 'technique',
      name: 'Kiri-oroshi',
      nameJa: '切下',
      techniqueId: 'kime-tachiai-kiri-oroshi',
      groupId: 'tachiai',
      criteria: [
        c('kime-tachiai-kiri-oroshi-1', 'Tori ~2.7 m from Uke starting position', 'small_medium'),
        c(
          'kime-tachiai-kiri-oroshi-2',
          'Uke draws slowly Seigan at eyes; tsugi-ashi forward; Tori one step back starting right',
          'small_medium',
        ),
        c(
          'kime-tachiai-kiri-oroshi-3',
          'Uke Jodan then slash head (kiai); sword over head before slash',
          'small_medium',
        ),
        c(
          'kime-tachiai-kiri-oroshi-4',
          'Tori left foot, 90° tai-sabaki deflect, wrist to hip, collar, R-L-R steps diagonally, hara-gatame',
          'small_medium',
        ),
        c(
          'kime-tachiai-kiri-oroshi-5',
          'Uke should not bend too much; taps leg twice; sword return sequence and Tori to original position',
          'small_medium',
        ),
      ],
    },
  ],
}
